import { createAsyncThunk } from "@reduxjs/toolkit";

import { ajax } from "../../../utils/ajax";
import { IBasket, ICustomerDetails } from "../models";

interface IBasketLookupParams {
    q: string;
}

interface ILookupBasketResult {
    basket: IBasket;
    owner: ICustomerDetails;
}

/**
 * Looks up a basket by its reference number and fetches the owner's details.
 *
 * This is typically used in the Basket Search panel to find specific baskets
 * by their encoded basket ID or reference number. After finding the basket,
 * it also fetches the full customer details of the basket owner.
 *
 * @param reference_number - The basket reference number or encoded basket ID to search for
 * @returns Promise resolving to basket and owner details
 * @throws Error if basket is not found or owner cannot be loaded
 *
 * @example
 * dispatch(lookupBasket("ABC123"))
 */
export const lookupBasket = createAsyncThunk<
    ILookupBasketResult,
    string,
    { rejectValue: string }
>("csr/lookupBasket", async (reference_number, { rejectWithValue }) => {
    try {
        const query: IBasketLookupParams = {
            q: reference_number,
        };
        const basket = (
            await ajax
                .get("/api/csr/baskets/lookup/")
                .set("Accept", "application/json")
                .query(query)
        ).body as IBasket;
        const owner = (
            await ajax.get(basket.owner).set("Accept", "application/json")
        ).body as ICustomerDetails;
        return {
            basket: basket,
            owner: owner,
        };
    } catch (err) {
        const error = err as {
            response?: { body?: { q?: unknown; detail?: unknown } };
            message?: string;
        };
        const message =
            typeof error.response?.body?.q === "string"
                ? error.response.body.q
                : typeof error.response?.body?.detail === "string"
                  ? error.response.body.detail
                  : typeof error.message === "string"
                    ? error.message
                    : "An error occurred while looking up the basket";
        return rejectWithValue(message);
    }
});
